import React from 'react'
import { Helmet } from 'react-helmet-async'
import { useInView } from 'react-intersection-observer'

import { Layout, Header } from 'app/theme/layout'
import LeadModal from 'app/leads/LeadModal'
import { useModalState } from 'lib/modal'
import NavbarFixed from 'app/theme/NavbarFixed'
import ImageHero from 'app/theme/ImageHero'
import { OccasionPageGrid, OccasionPageGridRow } from '../occasions/shared/OccasionPageGrid'
import CuisineTypes from 'app/theme/sections/CuisineTypes'
import HowItWorks from 'app/theme/sections/HowItWorks'
import HowItWorksLarge from 'app/theme/sections/HowItWorksLarge'
import WhyWorkWithUs from 'app/theme/sections/WhyWorkWithUs'
import FeaturedClients from 'app/theme/sections/FeaturedClients'
import Calculator from 'app/theme/calculator'

import heroImg from 'assets/img/bar-brewery-winery-hero.jpg'
import gridImage1 from 'assets/img/bar-brewery-winery-1.jpg'
import gridImage2 from 'assets/img/bar-brewery-winery-2.jpg'
import gridImage3 from 'assets/img/bar-brewery-winery-3.jpg'
import gridImage4 from 'assets/img/bar-brewery-winery-4.jpg'
import gridImage5 from 'assets/img/bar-brewery-winery-5.jpg'

export default function BarBreweryWineryPage() {
  const { openModal, ...modalState } = useModalState()

  const [ref, inView] = useInView({
    rootMargin: '1px',
    initialInView: true,
  })

  return (
    <Layout header={<Header occasionMenu />}>
      <Helmet>
        <title>Food Trucks For Bars, Breweries, and Wineries - Roaming Hunger</title>
        <meta name="og:title" content="Food Trucks For Bars, Breweries, and Wineries"></meta>
        <meta name="description" content="Across the country, local governments are requiring that bars, wineries, and breweries serve food if they offer alcohol. Book a local food truck to meet that requirement."></meta>
      </Helmet>
      <LeadModal
        {...modalState}
      />
      <div ref={ref}>
        <ImageHero
          imageUrl={heroImg}
          title="Food Trucks For Bars, Breweries, and Wineries"
          onButtonClick={openModal}
          description={(
            <div>
              With a network of over 18,000 food trucks across the US and Canada, Roaming Hunger
              can help you reach every food truck in your community. And with over 11 years
              experience booking trucks, we can manage the entire process from beginning to end.
            </div>
          )}
        />
      </div>
        <NavbarFixed
          onButtonClick={openModal}
          menuItems={[
            {
              title: 'Community Events',
              url: '/food-truck-catering/local-community-concessions/',
            },
            {
              title: '100% Roaming Hunger Event Guarantee',
              url: '/food-truck-catering/community-concessions-event-guarantee/#submenu',
            },
            {
              title: 'Food Trucks For Bars, Breweries, and Wineries',
              url: '/food-truck-catering/food-trucks-for-bars-breweries-wineries/#submenu',
              active: true
            }
          ]}
        />

      <div className="container text-center py-12 px-8">
        <h2 className="text-navy text-4xl leading-tight font-platform">
          Get Back to Business
        </h2>
        <p className="font-light text-gray-900 mt-6">
          Need food service to operate your bar, brewery, or winery? Book a local food truck
          to meet any city or state requirements during the Coronavirus pandemic. We’ll work
          with you to make sure your businesses gets back to doing what you do best,
          serving your customers.
        </p>
      </div>

      <OccasionPageGrid className="bg-white py-8">
        <OccasionPageGridRow
          heading="Keep Your Customers Safe"
          content="Enjoy great tasting local dishes without compromising safety. Our mobile food partners will work with you to ensure that social distancing practices are in effect. Additionally, all food is from a controlled, limited-access kitchen, subject to the Health Department’s highest scrutiny."
          image={gridImage1}
          reverse
        />
        <OccasionPageGridRow
          heading="The Easiest Way to Connect With Food Trucks"
          content="There’s no better way to reach nearly every food truck in your city. All you have to do is give us a few details about when you’d like the truck to show up, and we take care of the rest. You’ll only hear from interested trucks, and you can choose the best fit for your business and customers."
          image={gridImage2}
        />
      </OccasionPageGrid>

      <CuisineTypes baseUrl='/food-truck-catering' />

      <HowItWorks
        heading="How Food Trucks For Bars, Breweries, & Winery Catering Works"
        onButtonClick={openModal}
      />
      <HowItWorksLarge
        heading="How Food Trucks For Bars, Breweries, & Winery Catering Works"
        onButtonClick={openModal}
      />

      <OccasionPageGrid className="bg-white py-8">
        <OccasionPageGridRow
          heading="We'll Handle the Details"
          content="All the paperwork (Insurance, Health Permits, etc.) is collected so that you can focus on running your business. And if you ever need support, a dedicated account manager will ensure everything goes smoothly."
          image={gridImage3}
          reverse
        />
        <OccasionPageGridRow
          heading="Support Small Business"
          content="Nothing’s better than local businesses supporting one another. 98% of food trucks are small businesses owned or operated by chef entrepreneurs who are also feeling the effects of coronavirus."
          image={gridImage4}
        />
        <OccasionPageGridRow
          heading="Book as Many Truck as You Need"
          content="Get a food truck for one event or every day of the week. If you need an entire food truck program, we’ll get our team to build out a complete schedule and actively manage it to make sure the whole thing is a success."
          image={gridImage5}
          reverse
        />
      </OccasionPageGrid>
    </Layout>
  )
}
